import React, { ChangeEvent, FC, useCallback, memo } from 'react';
import { FieldProps, Field, useFormikContext } from 'formik';
import Input from '@minecraft.input';
import { setFieldValueAndTouched } from '@minecraft.utils';
import { useTranslation } from '@blocs.i18n';
import { RangeEnum, FilterData, FilterValues } from '../../shared/types';
import { FieldGroup, Delimiter, StyledCheckbox } from '../../shared/styles';
import RangeLabel from '../RangeLabel/RangeLabel';

type Props = {
  ageRangeName: FilterData['name'];
  switchAgeToMonthsName: FilterData['name'];
  ageMinName: FilterData['name'];
  ageMaxName: FilterData['name'];
  label: FilterData['label'];
  ageLimit?: number;
};

const AgeRange: FC<Props> = ({ ageRangeName, switchAgeToMonthsName, ageMinName, ageMaxName, label, ageLimit }) => {
  const { setFieldValue } = useFormikContext();
  const { t } = useTranslation();

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const currentInputName = event.currentTarget.name;
      const currentInputValue = event.currentTarget.value;
      const currentInputLimit = event.currentTarget.max;

      if (Number(currentInputValue) > Number(currentInputLimit)) {
        setFieldValue(currentInputName, currentInputLimit, false);
      } else {
        setFieldValue(currentInputName, currentInputValue, false);
      }
    },
    [setFieldValue]
  );

  return (
    <Field>
      {({ form }: FieldProps<FilterValues>): JSX.Element => {
        return (
          <>
            <RangeLabel
              switchTo={form.values[ageRangeName][switchAgeToMonthsName]}
              label={label}
              type={RangeEnum.age}
            />
            <FieldGroup>
              <Input
                name={`${ageRangeName}.${ageMinName}`}
                value={form.values[ageRangeName][ageMinName]}
                type="number"
                size="small"
                min={0}
                max={ageLimit}
                onChange={handleChange}
                aria-label={t('common:form.label.roleAgeRange.min')}
              />
              <Delimiter>{t('common:filter.to')}</Delimiter>
              <Input
                name={`${ageRangeName}.${ageMaxName}`}
                value={form.values[ageRangeName][ageMaxName]}
                type="number"
                size="small"
                min={0}
                max={ageLimit}
                onChange={handleChange}
                aria-label={t('common:form.label.roleAgeRange.max')}
              />
            </FieldGroup>
            <StyledCheckbox
              isChecked={form.values[ageRangeName][switchAgeToMonthsName]}
              name={`${ageRangeName}.${switchAgeToMonthsName}`}
              label={t('common:filter.switchToMonths')}
              onChange={(): void => {
                setFieldValueAndTouched(
                  `${ageRangeName}.${switchAgeToMonthsName}`,
                  !form.values[ageRangeName][switchAgeToMonthsName],
                  form
                );
              }}
            />
          </>
        );
      }}
    </Field>
  );
};

export default memo(AgeRange);
